import { getByPath } from "./get-by-path";

interface FileValidationError {
  message: string;
  filename: string;
}

export const getFileUploadErrorMessage = (
  error?: unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- a needed evil since we don't have i18n types installed globally
  tFunction?: (key: string, options: Record<string, any>) => string,
  tFunctionCrm?: (key: string, options: Record<string, string>) => string
) => {
  const errorData = getByPath(error, ["response", "data", "error"]);
  const details = getByPath(errorData, ["details"]);

  const { pwdProtectedFilenames, malformedFilenames } = (
    Array.isArray(details) ? [...details] : [details]
  ).reduce(
    (acc, failedFile: FileValidationError) => {
      if (failedFile?.message.includes("password_protected"))
        acc.pwdProtectedFilenames.push(failedFile.filename);

      if (failedFile?.message.includes("malformed_pdf"))
        acc.malformedFilenames.push(failedFile.filename);

      return acc;
    },
    {
      pwdProtectedFilenames: [],
      malformedFilenames: []
    }
  );

  if (pwdProtectedFilenames.length || malformedFilenames.length) {
    const pwdProtectedErrorMessage = getPwdProtectedErrorMessage(
      pwdProtectedFilenames,
      tFunction,
      tFunctionCrm
    );
    const malformedFilesErrorMessage = getMalformedFilesErrorMessage(
      malformedFilenames,
      tFunction,
      tFunctionCrm
    );

    return [pwdProtectedErrorMessage, malformedFilesErrorMessage]
      .filter(Boolean)
      .join(" ");
  }

  return null;
};

const getPwdProtectedErrorMessage = (
  filenames: string[],
  tFunction?: (key: string, options: Record<string, unknown>) => string,
  tFunctionCrm?: (key: string, options: Record<string, string>) => string
) => {
  switch (filenames.length) {
    case 0:
      return "";
    case 1:
      return (
        tFunction?.("file-upload-validation-errors.password-protected-one", {
          name: filenames[0]
        }) ||
        tFunctionCrm?.("password-protected-one", {
          name: filenames[0]
        }) ||
        `Document "${filenames[0]}" is a password-protected PDF and cannot be processed. Please upload a version that does not require a password.`
      );
    default: {
      const joinedFileNames = filenames.join(", ");

      return (
        tFunction?.("file-upload-validation-errors.password-protected-many", {
          names: joinedFileNames
        }) ||
        tFunctionCrm?.("password-protected-many", {
          names: joinedFileNames
        }) ||
        `Documents "${joinedFileNames}" are password-protected PDFs and cannot be processed. Please upload versions that do not require a password.`
      );
    }
  }
};

const getMalformedFilesErrorMessage = (
  filenames: string[],
  tFunction?: (key: string, options: Record<string, unknown>) => string,
  tFunctionCrm?: (key: string, options: Record<string, string>) => string
) => {
  switch (filenames.length) {
    case 0:
      return "";
    case 1:
      return (
        tFunction?.("file-upload-validation-errors.malformed-one", {
          name: filenames[0]
        }) ||
        tFunctionCrm?.("malformed-one", {
          name: filenames[0]
        }) ||
        `Document "${filenames[0]}" is malformed and cannot be processed. Please generate and upload a new document to try to resolve this error.`
      );
    default: {
      const joinedFileNames = filenames.join(", ");

      return (
        tFunction?.("file-upload-validation-errors.malformed-many", {
          names: joinedFileNames
        }) ||
        tFunctionCrm?.("malformed-many", {
          names: joinedFileNames
        }) ||
        `Documents "${joinedFileNames}" are malformed and cannot be processed. Please generate and upload new documents to try to resolve this error.`
      );
    }
  }
};
